import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { api } from "../api/client";
import type { ListResponse, Venue } from "../api/types";
import { useAuth } from "../auth/AuthProvider";

// The venue a person is scoping their view to. Every screen outside Finance and Analytics
// shows exactly one venue at a time, so the scope always resolves to a real venue rather
// than an unscoped "all". Like the workspace switch this stores a preference and clamps it
// to what the membership actually allows, so revoking a venue takes effect on the next
// render rather than on the next time somebody clears their storage.

function readStoredVenue(): string | null {
  try {
    const stored = window.localStorage.getItem("venueops-venue-scope");
    // "all" was a legal stored value before single-venue scoping; treat it as no choice.
    return stored === "all" ? null : stored;
  } catch {
    return null;
  }
}

function writeStoredVenue(venueId: string): void {
  try {
    window.localStorage.setItem("venueops-venue-scope", venueId);
  } catch {
    // The choice still applies to this session; it just will not survive a reload.
  }
}

interface VenueScopeChoice {
  choice: string | null;
  choose(venueId: string): void;
}

const VenueScopeChoiceContext = createContext<VenueScopeChoice>({
  choice: null,
  choose: () => undefined,
});

export function VenueScopeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<string | null>(readStoredVenue);
  const choose = useCallback((venueId: string) => {
    setChoice(venueId);
    writeStoredVenue(venueId);
  }, []);
  const value = useMemo(() => ({ choice, choose }), [choice, choose]);
  return (
    <VenueScopeChoiceContext.Provider value={value}>{children}</VenueScopeChoiceContext.Provider>
  );
}

export interface VenueScopeState {
  // The venue being filtered to. Empty only while the venue list is loading or when the
  // membership has no venues at all; pages should treat an empty id as not ready yet.
  selectedVenueId: string;
  // The venues this person may see. Empty while loading or when no membership exists.
  venues: Venue[];
  // Whether the venue list is still loading.
  loading: boolean;
  // Whether the dropdown should render (more than one venue available).
  showSelector: boolean;
  // The display name for the current selection.
  label: string;
  setVenueId(venueId: string): void;
}

export function useVenueScope(): VenueScopeState {
  const auth = useAuth();
  const { choice, choose } = useContext(VenueScopeChoiceContext);
  const membership = auth.session?.selectedMembership;

  const venuesQuery = useQuery({
    queryKey: ["venues"],
    queryFn: () => api<ListResponse<Venue>>("/api/v1/venues"),
    enabled: Boolean(membership),
    staleTime: 30_000,
  });

  const venues = venuesQuery.data?.items ?? [];
  const loading = venuesQuery.isLoading;

  // Somebody with exactly one venue has nothing to choose from: the dropdown would show a
  // single option, which suggests there is more of the product waiting for them than there is.
  const showSelector = venues.length > 1;

  // Clamp the stored choice to what is actually available, like workspace.tsx does for the
  // workspace switch. A stored venue that no longer appears in the list, and the absence of
  // any stored choice, both fall back to the first venue the membership can see.
  const chosen = choice && venues.some((v) => v.id === choice) ? choice : "";
  const selectedVenueId = chosen || venues[0]?.id || "";

  const label = selectedVenueId
    ? (venues.find((v) => v.id === selectedVenueId)?.name ?? "Select venue")
    : loading
      ? "Loading venues..."
      : "No venues";

  return { selectedVenueId, venues, loading, showSelector, label, setVenueId: choose };
}
